/**
 * Client service for Construction Quality & Snags.
 * Inspections, pour / pre-concealment cards, test samples and results.
 * R39 B4.a + B4.b (plan docs/plans/sector-operations.md row B4 :64).
 * Wraps callables via callable(); components never import firebase/*.
 */

import { callable } from './callable'
import type {
  OpsRecord,
  ConHoldState,
  ConInspectionOutcome,
  ConTestOutcome,
  ConPourCardBlock,
  ConTestDoc,
  SaveConPourCardInput,
  RequestConInspectionInput,
  RecordConInspectionOutcomeInput,
  ReassignConInspectorInput,
  GetConPourCardInput,
  ListConPourCardsInput,
  CreateConTestInput,
  AddConTestResultInput,
  ListConTestsInput,
} from '../types/ops'

export async function saveConPourCard(
  input: SaveConPourCardInput,
): Promise<{ cardId: string; holdState: ConHoldState; replayed?: boolean }> {
  return callable<SaveConPourCardInput, { cardId: string; holdState: ConHoldState; replayed?: boolean }>(
    'saveConPourCard',
    input,
  )
}

export async function requestConInspection(
  input: RequestConInspectionInput,
): Promise<{ cardId: string; requestId: string; holdState: ConHoldState; replayed?: boolean }> {
  return callable<RequestConInspectionInput, { cardId: string; requestId: string; holdState: ConHoldState; replayed?: boolean }>(
    'requestConInspection',
    input,
  )
}

export async function recordConInspectionOutcome(
  input: RecordConInspectionOutcomeInput,
): Promise<{
  requestId: string
  outcome: ConInspectionOutcome
  holdState: ConHoldState
  ncrRequestId?: string | null
  requestClosed?: boolean
  replayed?: boolean
}> {
  return callable<
    RecordConInspectionOutcomeInput,
    {
      requestId: string
      outcome: ConInspectionOutcome
      holdState: ConHoldState
      ncrRequestId?: string | null
      requestClosed?: boolean
      replayed?: boolean
    }
  >('recordConInspectionOutcome', input)
}

export async function reassignConInspector(
  input: ReassignConInspectorInput,
): Promise<{ cardId: string; newInspectorUid: string; replayed?: boolean }> {
  return callable<ReassignConInspectorInput, { cardId: string; newInspectorUid: string; replayed?: boolean }>(
    'reassignConInspector',
    input,
  )
}

export async function getConPourCard(
  input: GetConPourCardInput,
): Promise<{ card: OpsRecord; pour: ConPourCardBlock; canRecordOutcome?: boolean }> {
  return callable<GetConPourCardInput, { card: OpsRecord; pour: ConPourCardBlock; canRecordOutcome?: boolean }>(
    'getConPourCard',
    input,
  )
}

export async function listConPourCards(
  input: ListConPourCardsInput,
): Promise<{ records: Array<OpsRecord & { pour?: ConPourCardBlock }> }> {
  return callable<ListConPourCardsInput, { records: Array<OpsRecord & { pour?: ConPourCardBlock }> }>(
    'listConPourCards',
    input,
  )
}

export async function createConTest(
  input: CreateConTestInput,
): Promise<{ testId: string; outcome: ConTestOutcome; replayed?: boolean }> {
  return callable<CreateConTestInput, { testId: string; outcome: ConTestOutcome; replayed?: boolean }>(
    'createConTest',
    input,
  )
}

export async function addConTestResult(
  input: AddConTestResultInput,
): Promise<{ testId: string; outcome: ConTestOutcome; ncrRequestId?: string | null; replayed?: boolean }> {
  return callable<
    AddConTestResultInput,
    { testId: string; outcome: ConTestOutcome; ncrRequestId?: string | null; replayed?: boolean }
  >('addConTestResult', input)
}

export async function listConTests(
  input: ListConTestsInput,
): Promise<{ tests: ConTestDoc[] }> {
  return callable<ListConTestsInput, { tests: ConTestDoc[] }>(
    'listConTests',
    input,
  )
}
